import React from 'react';

interface BusinessReportPlainProps {
  title: string;
  content: string;
  companyName?: string;
}

/**
 * Component for generating plain text business reports with minimal formatting
 */
export const BusinessReportPlain: React.FC<BusinessReportPlainProps> = ({ 
  title, 
  content, 
  companyName = 'Su Empresa' 
}) => {
  // Format the content into a structured plain text document
  const formatPlainText = (): string => {
    if (!content || content.trim() === '') {
      return 'No se pudo recuperar texto del documento.';
    }
    
    const lines = content.split(/\r?\n/).filter(line => line.trim() !== '');
    const documentTitle = title || (lines.length > 0 ? lines[0] : 'Documento Empresarial');
    
    // Current date formatted
    const currentDate = new Date().toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    // Create header
    let formattedText = `${documentTitle.toUpperCase()}\n`;
    formattedText += '='.repeat(documentTitle.length) + '\n\n';
    formattedText += `Fecha: ${currentDate}\n\n`;
    
    // Process content
    const contentLines = title ? lines : (lines.length > 1 ? lines.slice(1) : []);
    
    contentLines.forEach(line => {
      const trimmedLine = line.trim();
      
      // Detect if it's a possible subtitle (short line without a final period)
      if (trimmedLine.length < 50 && !trimmedLine.endsWith('.') && !trimmedLine.endsWith('?') && !trimmedLine.endsWith('!')) {
        formattedText += `\n${trimmedLine}\n`;
        formattedText += '-'.repeat(trimmedLine.length) + '\n\n';
      } 
      // Detect if it's a list item
      else if (trimmedLine.match(/^[\d\-\*\•][\.\)\-\s]/)) {
        formattedText += `  • ${trimmedLine.replace(/^[\d\-\*\•][\.\)\-\s]/, '')}\n`;
      }
      // Normal paragraph 
      else {
        formattedText += `${trimmedLine}\n\n`;
      }
    });
    
    // Add footer
    formattedText += '\n\n';
    formattedText += '---\n';
    formattedText += 'Documento generado automáticamente\n';
    formattedText += `© ${new Date().getFullYear()} ${companyName}\n`;
    
    return formattedText;
  };

  const downloadPlainText = () => {
    try {
      const textContent = formatPlainText();
      const blob = new Blob([textContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      a.download = `informe-${timestamp}.txt`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading plain text report:', error);
      alert('Error al descargar el informe de texto plano');
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold mb-3 text-gray-700">Informe de Texto Plano</h3>
      <p className="text-sm text-gray-600 mb-4">
        Genera un documento de texto plano con formato básico empresarial,
        ideal para compartir información de forma sencilla y compatible con cualquier sistema.
      </p>
      
      <div className="flex flex-col space-y-3">
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 w-24">Título:</span>
          <span className="text-sm text-gray-600">{title || 'Documento Empresarial'}</span>
        </div>
        
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 w-24">Empresa:</span>
          <span className="text-sm text-gray-600">{companyName}</span>
        </div>
        
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 w-24">Contenido:</span>
          <span className="text-sm text-gray-600 truncate">{content.substring(0, 50)}...</span>
        </div>
      </div>
      
      <button
        onClick={downloadPlainText}
        className="mt-4 w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center"
      >
        Descargar Texto Plano
      </button>
    </div>
  );
};